import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import ListaArquivos from "../ListaArquivos";
import ImagesList from "../ImagesList";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import { toast } from "react-toastify";
import { ChamadoType, novaMensagem } from "../../types/notifications/types";
import { trackEventMatomo } from "../../helpers/matomo";

interface EmAndamentoProps {
  t: (key: string) => string;
  data: ChamadoType;
  objectStatus: novaMensagem;
  arquivos: File[]
  images: File[],
  setObjectStatus: React.Dispatch<React.SetStateAction<novaMensagem>>;
  disable: boolean;
  executeAction: (action: string) => void;
  selectedAction: string;
  priority: string;
  setPriority: React.Dispatch<React.SetStateAction<string>>;
  confirmationLabel: string;
  verificaResponsavel: () => boolean;
  setArquivos: Function,
  setImages: Function,
}

export default function EmAndamento({
  t,
  data,
  objectStatus,
  arquivos,
  images,
  setObjectStatus,
  disable,
  executeAction,
  selectedAction,
  priority,
  setPriority,
  confirmationLabel,
  verificaResponsavel,
  setArquivos,
  setImages
}: EmAndamentoProps) {
  const [openMapFiles, setOpenMapFiles] = useState(false);
  const [openMapImages, setOpenMapImages] = useState(false);

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1
  });

  function handleOpenFiles() {
    trackEventMatomo('chamado', 'click', 'lista de arquivos', openMapFiles ? 'recolhe arquivos' : 'expande arquivos');
    setOpenMapFiles(!openMapFiles);
  }

  function handleOpenImages() {
    trackEventMatomo('chamado', 'click', 'lista de imagens', openMapImages ? 'recolhe imagens' : 'expande imagens');
    setOpenMapImages(!openMapImages);
  }
  
  return (
    <>
      <Typography variant="h6" textAlign="center">
        {t("ticketDetail.doAction")}
      </Typography>

      <Stack
        width="100%"
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1.5}
      >
        <Stack
          paddingBottom={1}
          width="100%"
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DesktopDatePicker
              disabled={disable}
              format="dd/MM/yyyy"
              label={t("ticketDetail.dateConclusion")}
              value={objectStatus.dataAcompanhamento as Date}
              onChange={(date) => {
                trackEventMatomo('chamado', 'click', 'seletor de data', 'define data de conclusão');
                setObjectStatus((prev) => ({
                  ...prev,
                  dataAcompanhamento: date?.setHours(23, 59, 59) as number
                }));
              }}
              slotProps={{
                field: { readOnly: true }
              }}
            />
          </LocalizationProvider>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t("ticketDetail.comment")}
            onFocus={() => trackEventMatomo('chamado', 'click', 'campo de texto', 'edita comentário')}
            onChange={(e) =>
              setObjectStatus((prev) => ({
                ...prev,
                comentario: e.target.value
              }))
            }
            value={objectStatus.comentario}
            disabled={disable}
          />
          {selectedAction === "priority" && (
            <FormControl fullWidth disabled={disable}>
              <InputLabel id="ticket-priority-label">{t("Reports.modalDetailP.priority")}</InputLabel>
              <Select
                labelId="ticket-priority-label"
                label={t("Reports.modalDetailP.priority")}
                value={priority}
                onChange={event => {
                  trackEventMatomo('chamado', 'click', 'select', 'define prioridade');
                  setPriority(event.target.value);
                }}
              >
                <MenuItem value="alta">{t("notifications.myTickets.high")}</MenuItem>
                <MenuItem value="media">{t("notifications.myTickets.medium")}</MenuItem>
                <MenuItem value="baixa">{t("notifications.myTickets.low")}</MenuItem>
              </Select>
            </FormControl>
          )}
        </Stack>
        <Divider variant="middle" orientation="vertical" flexItem />
        <Stack width="100%" direction="column" justifyContent="flex-start" spacing={1}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" width="100%">
            <Button
              disabled={disable || arquivos.length >= 20}
              component="label"
              variant="contained"
              endIcon={<UploadFileIcon />}>
              {t("notifications.ticket.sendFiles")}
              <VisuallyHiddenInput
                disabled={disable}
                accept="video/*, .doc, .docx, .xls, .xlsx, audio/*, .pdf, .txt"
                type="file"
                multiple
                onChange={(event) => {
                  if (!event.target.files) return;
                  trackEventMatomo('chamado', 'click', 'upload', 'adiciona arquivos');
                  const novos = Array.from(event.target.files);
                  const tamanhoMaxIndividual = 20 * 1024 * 1024; // 20MB
                  const tamanhoMaxTotal = 100 * 1024 * 1024; // 100MB

                  const totalArquivos = arquivos.length + novos.length;

                  // soma atual dos arquivos no estado
                  const tamanhoAtual = arquivos.reduce((acc, file) => acc + file.size, 0);

                  // filtra arquivos válidos
                  const validos = novos.filter((file) => {
                    if (file.size > tamanhoMaxIndividual) {
                      toast.warning(t("notifications.ticket.upload.fileTooLarge").replace("{{name}}", file.name));
                      return false;
                    }
                    return true;
                  });
                  // verifica limite total
                  const totalNovo = tamanhoAtual + validos.reduce((acc, f) => acc + f.size, 0);
                  if (totalNovo > tamanhoMaxTotal) {
                    toast.error(t("notifications.ticket.upload.totalSizeExceeded"));
                    return;
                  }
                  if (totalArquivos > 20) {
                    toast.error(t("notifications.ticket.upload.tooManyFiles"));
                    return;
                  }

                  setArquivos((prev: File[]) => [...prev, ...validos]);
                }}
              />
            </Button>
            <Button
              disabled={disable || images?.length >= 20}
              color="secondary"
              component="label"
              variant="contained"
              style={{ color: 'white' }}
              endIcon={<AddPhotoAlternateIcon />}
            >
              {t("notifications.ticket.sendPhotos")}
              <VisuallyHiddenInput
                disabled={disable}
                accept="image/jpg, .jpeg, .png, .gif, .tiff, .tif, .svg, .webp, .heic, .heif, .bmp"
                type="file"
                multiple
                onChange={(event) => {
                  if (!event.target.files) return;
                  trackEventMatomo('chamado', 'click', 'upload', 'adiciona imagens');
                  const novos = Array.from(event.target.files);
                  const tamanhoMaxIndividual = 20 * 1024 * 1024; // 20MB
                  const tamanhoMaxTotal = 100 * 1024 * 1024; // 100MB

                  const totalImagens = images.length + novos.length;

                  // soma atual dos arquivos no estado
                  const tamanhoAtual = images.reduce((acc, file) => acc + file.size, 0);

                  // Filtra arquivos válidos
                  const validos = novos.filter((file) => {
                    // Verifica se o tipo do arquivo está dentro dos tipos permitidos
                    const tipoPermitido = /^(image\/jpg|image\/jpeg|image\/png|image\/gif|image\/tiff|image\/svg\+xml|image\/webp|image\/heic|image\/heif|image\/bmp)$/i;
                    if (!tipoPermitido.test(file.type)) {
                      toast.warning(t("notifications.ticket.upload.invalidImageType").replace("{{name}}", file.name));
                      return false;
                    }

                    // Verifica se o tamanho do arquivo é maior que o permitido
                    if (file.size > tamanhoMaxIndividual) {
                      toast.warning(t("notifications.ticket.upload.imageTooLarge").replace("{{name}}", file.name));
                      return false;
                    }

                    return true;
                  });

                  // Verifica o limite de tamanho total
                  const totalNovo = tamanhoAtual + validos.reduce((acc, f) => acc + f.size, 0);
                  if (totalNovo > tamanhoMaxTotal) {
                    toast.error(t("notifications.ticket.upload.totalSizeExceeded"));
                    return;
                  }

                  // Verifica o limite de imagens
                  if (totalImagens > 20) {
                    toast.error(t("notifications.ticket.upload.tooManyImages"));
                    return;
                  }

                  // Atualiza o estado com as imagens válidas
                  setImages((prev: File[]) => [...prev, ...validos]);
                }}
              />
            </Button>
          </Stack>
          {arquivos.length > 0 && (
            <ListaArquivos
              arquivos={arquivos}
              setArquivos={setArquivos}
              open={openMapFiles}
              setOpen={handleOpenFiles}
              disabled={disable}
            />
          )}
          {images.length > 0 && (
            <ImagesList
              images={images}
              setImages={setImages}
              open={openMapImages}
              setOpen={handleOpenImages}
              disabled={disable}
            />
          )}
        </Stack>
      </Stack>
      <Stack justifyContent="flex-end" alignItems="center" width="100%" direction="row">
        <Button
          className="button is-success"
          endIcon={<CheckCircleOutlineIcon />}
          disabled={disable || data?.block || !selectedAction || (selectedAction === "priority" && !priority)}
          onClick={() => {
            trackEventMatomo('chamado', 'click', 'botão', 'confirma ação');
            executeAction(selectedAction);
          }}
        >
          {confirmationLabel}
        </Button>
      </Stack>
      {!verificaResponsavel() && (
        <span className="tag is-warning">
        {t("notifications.ticket.onlyResponsibleCanComplete")} "
          {data.responsavelNome || t("notifications.ticket.nameNotFound")}"{" "}
          {t("notifications.ticket.canCompleteThisTicket")}
        </span>
      )}
    </>
  );
}
